import { useLazyQuery } from "@apollo/client";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { GET_CITIES } from "@api/queries/suggestions";
import { GET_ALL_PAGES_USER } from "@api/queriesV2/cms";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { getPageTypes } from "@config/cms";
import { useStore } from "@hooks/useZustandProvider";
import Container from "@ui-kit/Container/Container";
import Loading from "@ui-kit/Loading/Loading";
import {
  resolveRouteCountryBucketKey,
  sortDestinationCountryEntriesByLocale,
} from "@utils/countryAlpha2";

import s from "./AllDestinationsBlock.module.scss";
import Destination from "./components/Destination/Destination";

const Country = ({ countryName, cities }) => {
  const { t } = useTranslation();

  return (
    <div className={s.country}>
      <div className={s.name}>
        <div className={s.flag}>
          <Image
            fill
            style={{
              objectFit: "cover",
              objectPosition: "center",
            }}
            src={`/static/images/svg/flags/flag-${countryName.toLowerCase()}.svg`}
            alt={`Flag ${countryName.toLowerCase()}`}
          />
        </div>
        <h3>{t(countryName)}</h3>
      </div>

      <div className={s.directions}>
        {cities.map((destination, key) => (
          <Destination destination={destination} key={key} />
        ))}
      </div>
    </div>
  );
};

const AllDestinationsBlock = ({ settings, data, updateField }) => {
  const { t } = useTranslation();
  const { cms, getBlockLocale, user } = useStore();
  const { isEditMode } = cms.uiOptions;
  const { isStaff } = user;
  const { lang } = cms.page;
  const { locale } = useRouter();
  const { title } = data;
  const { blockType } = settings.common;
  const [pages, setPages] = useState([]);
  const [coutries, setCoutries] = useState({});
  const pagesRef = useRef([]);
  const citiesQueryLangRef = useRef(null);

  const [getCities, { loading: citiesLoading }] = useLazyQuery(GET_CITIES, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      try {
        const SORTED_COUNTRIES = {
          Estonia: [],
          Latvia: [],
          Lithuania: [],
          Russia: [],
          Poland: [],
          Finland: [],
        };

        pagesRef.current.forEach((page) => {
          const cityId = page.properties.destination;

          const getCity = (cityId) =>
            data.cities.find((city) => city.CityId === Number(cityId));
          const city = getCity(cityId);

          if (!city) {
            if (isStaff) {
              console.log(`Page info with city ID: ${cityId}:`, page);

              return toast.warn(
                `[Admin] The city (destination) with ID ${cityId} was not received from the backend. See more details in console.`,
              );
            }

            return;
          }

          const rawCountryName = city.BusStops[0].CountryName;
          const countryBucket = resolveRouteCountryBucketKey(
            rawCountryName,
            citiesQueryLangRef.current,
          );
          const countryRoutes =
            countryBucket && SORTED_COUNTRIES[countryBucket];

          if (!countryRoutes) {
            if (isStaff) {
              toast.warn(
                `[Admin] Unknown destination country bucket for "${rawCountryName}" (normalized: "${countryBucket}").`,
              );
            }

            return;
          }

          const cityName = city.CityName;

          countryRoutes.push({
            slug: page.data.slug,
            img: page.data.seo.thumbnail,
            countryName: countryBucket,
            cityName,
          });
        });

        setCoutries(SORTED_COUNTRIES);
      } catch (error) {
        console.log("error", error);
      }
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const [getAllPagesUser, { loading: pagesLoading }] = useLazyQuery(
    GET_ALL_PAGES_USER,
    {
      context: { clientName: "v2" },
      variables: {
        language: getBlockLocale(locale),
        type: getPageTypes().destination,
      },
      onCompleted: (data) => {
        try {
          const error = data?.error;

          if (data.error) {
            return toast.error(`[${error.ErrorCode}]: ${error.ErrorReason}`);
          }

          const pages = [...(data?.getAllPagesUser || [])]
            .filter((page) => !!page.properties.destination)
            .sort(
              (a, b) => a.properties.destination - b.properties.destination,
            );

          setPages(pages);
          pagesRef.current = pages;

          let ids = [];
          pages.forEach((page) => ids.push(page.properties.destination));
          ids = [...new Set(ids)].sort((a, b) => a - b);

          const citiesLang = getBlockLocale(locale);
          citiesQueryLangRef.current = citiesLang;

          getCities({
            variables: {
              lang: citiesLang,
              ids,
            },
          });
        } catch (error) {
          console.log("error", error);
        }
      },
      onError: (error) => {
        toast.error(`[${error.name}]: ${error.message}`);
      },
    },
  );

  useEffect(() => {
    getAllPagesUser();
  }, [isEditMode, lang, locale]);

  return (
    <Container className={s.allDestinationsBlock}>
      <Container full={false}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          {pagesLoading || citiesLoading ? (
            <Loading />
          ) : (
            sortDestinationCountryEntriesByLocale(
              Object.entries(coutries),
              locale,
            ).map(([country, cities]) => {
              return (
                <Country countryName={country} cities={cities} key={country} />
              );
            })
          )}
        </section>
      </Container>
    </Container>
  );
};

export default AllDestinationsBlock;
